import React, { createContext, useState, useContext, useCallback } from 'react';
import { track } from '../analytics/analytics';

const RecentlyAddedCompaniesContext = createContext();

export const useRecentlyAddedCompanies = () => useContext(RecentlyAddedCompaniesContext);

export const RecentlyAddedCompaniesProvider = ({ children }) => {
  const [recentlyAddedCompanies, setRecentlyAddedCompanies] = useState([]);

  const addRecentlyAddedCompany = useCallback((company) => {
    if (!company?.companyId || !company?.tenantId) return;
    // Every successful company add funnels through here — record it on the user's
    // journey so "who added <company>" is answerable in Product Analytics.
    track('add_company', {
      companyId: company?.companyId || null,
      companyName: company?.companyName || null,
      segment: company?.segment?.name || company?.segment?.id || null,
    });
    setRecentlyAddedCompanies((prev) => {
      const existing = prev.find((entry) => entry.tenantId === company.tenantId && entry.companyId === company.companyId);
      // A retried add must not restart the assessment clock or replace a newer
      // lifecycle with the create response from an earlier request.
      return existing ? prev : [...prev, { ...company, addedAt: company.addedAt || new Date().toISOString() }];
    });
  }, []);

  const clearRecentlyAddedCompanies = useCallback(() => {
    setRecentlyAddedCompanies([]);
  }, []);

  const removeRecentlyAddedCompanies = useCallback((tenantId, companyIds) => {
    const ids = new Set(companyIds || []);
    setRecentlyAddedCompanies((prev) => {
      const next = prev.filter((company) => company.tenantId !== tenantId || !ids.has(company.companyId));
      return next.length === prev.length ? prev : next;
    });
  }, []);

  return (
    <RecentlyAddedCompaniesContext.Provider value={{ recentlyAddedCompanies, addRecentlyAddedCompany, clearRecentlyAddedCompanies, removeRecentlyAddedCompanies }}>
      {children}
    </RecentlyAddedCompaniesContext.Provider>
  );
};
